import React, { useState } from 'react';

function HomeScreen() {
  // Form submission state: 'idle' | 'sending' | 'success' | 'error'
  const [status, setStatus] = useState('idle');

  // Smooth-scroll to a section by id
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Encode form data for Netlify Forms
  const encode = (data) => {
    return Object.keys(data)
      .map(key => encodeURIComponent(key) + '=' + encodeURIComponent(data[key]))
      .join('&');
  };

  // Handle contact form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    setStatus('sending');
    const form = e.target;
    const formData = new FormData(form);
    const data = {};
    formData.forEach((value, key) => { data[key] = value; });

    fetch('/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: encode(data),
    })
      .then(() => {
        setStatus('success');
        form.reset();
      })
      .catch(() => {
        setStatus('error');
      });
  };

  return (
    <div>
      {/* ============================================================
          NAVIGATION
          ============================================================ */}
      <nav className="av-nav">
        <div className="av-container av-nav-inner">
          <div className="av-brand">AddVentures</div>
          <div className="av-nav-links">
            <button className="av-nav-link" onClick={() => scrollTo('services')}>Services</button>
            <button className="av-nav-link" onClick={() => scrollTo('why')}>Approach</button>
            <button className="av-nav-link" onClick={() => scrollTo('who')}>Who we work with</button>
            <button className="av-nav-link" onClick={() => scrollTo('contact')}>Contact</button>
          </div>
          <button className="av-nav-cta" onClick={() => scrollTo('contact')}>
            Start a conversation
          </button>
        </div>
      </nav>

      {/* ============================================================
          HERO
          ============================================================ */}
      <section className="av-hero">
        <div className="av-container">
          <div className="av-hero-eyebrow">Strategic Finance · Capital · Advisory</div>
          <h1 className="av-hero-title">
            Senior finance leadership for founders <em>building serious companies.</em>
          </h1>
          <p className="av-hero-sub">
            AddVentures partners with founders and SME leaders who need the rigor of an institutional CFO and the speed of an in-house operator, without the cost of a full-time hire.
          </p>
          <div className="av-hero-ctas">
            <button className="av-btn-primary" onClick={() => scrollTo('contact')}>Start a conversation</button>
            <button className="av-btn-secondary" onClick={() => scrollTo('services')}>See what we do</button>
          </div>
        </div>
      </section>

      {/* ============================================================
          CREDIBILITY ROW
          ============================================================ */}
      <section className="av-credibility">
        <div className="av-container">
          <div className="av-cred-grid">
            <div>
              <div className="av-cred-num">10+</div>
              <div className="av-cred-label">Years senior finance experience</div>
            </div>
            <div>
              <div className="av-cred-num">6</div>
              <div className="av-cred-label">Specialized practice areas</div>
            </div>
            <div>
              <div className="av-cred-num">3</div>
              <div className="av-cred-label">Strategic priorities</div>
            </div>
            <div>
              <div className="av-cred-num">2 to 6 wks</div>
              <div className="av-cred-label">Typical engagement timeline</div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================
          SERVICES
          ============================================================ */}
      <section id="services" className="av-services">
        <div className="av-container">
          <div className="av-section-eyebrow">01 / What we do</div>
          <h2 className="av-section-title">Six practice areas, three strategic priorities.</h2>
          <p className="av-section-intro">
            We focus on the highest-stakes financial decisions a founder makes, the work that determines whether the company raises, scales, or sells on its own terms.
          </p>
          <div className="av-services-grid">
            <div className="av-service-card">
              <div className="av-service-num">/01 · STRATEGIC FINANCE</div>
              <h3>Financial Modeling</h3>
              <p>Three-statement, scenario-driven models built to tell your story in numbers and help stakeholders see the company through the right lens. Designed to serve founders and investors equally.</p>
            </div>
            <div className="av-service-card">
              <div className="av-service-num">/02 · CAPITAL</div>
              <h3>Pitch Deck</h3>
              <p>A narrative-driven deck built around what investors actually evaluate at your stage, structured from your data, your traction, and your story.</p>
            </div>
            <div className="av-service-card">
              <div className="av-service-num">/03 · CAPITAL</div>
              <h3>Data Room Setup</h3>
              <p>A complete, organized data room ready for due diligence: financials, contracts, cap table, IP, and operational documentation, structured the way professional investors expect.</p>
            </div>
            <div className="av-service-card">
              <div className="av-service-num">/04 · CAPITAL</div>
              <h3>Cap Table</h3>
              <p>Clean equity structures, dilution modeling, and option pool planning, ready for any round, any new hire, any due diligence, any exit.</p>
            </div>
            <div className="av-service-card">
              <div className="av-service-num">/05 · GROWTH</div>
              <h3>M&amp;A Advisory</h3>
              <p>Support for buy-side and sell-side transactions: target identification, deal structuring, and integration planning.</p>
            </div>
            <div className="av-service-card">
              <div className="av-service-num">/06 · GROWTH</div>
              <h3>Market Study</h3>
              <p>Structured analysis of market size, segmentation, competitive dynamics, and entry economics, giving founders and boards a defensible basis for strategic decisions.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================
          WHY US
          ============================================================ */}
      <section id="why" className="av-whyus">
        <div className="av-container">
          <div className="av-section-eyebrow">02 / Why founders choose us</div>
          <h2 className="av-section-title">Operator-led, not consultant-led.</h2>
          <p className="av-section-intro">
            Most advisory shops in this region are extensions of brokers, banks, or accounting firms. We are operators who have built finance functions, run cap tables through difficult rounds, and sat on the founder's side of the table.
          </p>
          <div className="av-why-grid">
            <div className="av-why-item">
              <div className="av-why-num">i.</div>
              <h4>Senior, not delegated.</h4>
              <p>The senior person you meet is the senior person doing the work. No bait and switch to junior associates after the engagement starts.</p>
            </div>
            <div className="av-why-item">
              <div className="av-why-num">ii.</div>
              <h4>Speed of an operator.</h4>
              <p>We move at the pace of a company in the middle of a raise, not the pace of an audit firm. Most engagements deliver within two to six weeks.</p>
            </div>
            <div className="av-why-item">
              <div className="av-why-num">iii.</div>
              <h4>Aligned to outcomes.</h4>
              <p>We measure success by whether you closed the round, won the mandate, or made the decision with confidence, not by hours billed.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================
          WHO WE WORK WITH
          ============================================================ */}
      <section id="who" className="av-who">
        <div className="av-container">
          <div className="av-section-eyebrow">03 / Who we work with</div>
          <h2 className="av-section-title">A good fit looks like one of three profiles.</h2>
          <p className="av-section-intro">If you recognize yourself in any of these, we should talk.</p>
          <div className="av-who-grid">
            <div className="av-who-card">
              <div className="av-who-marker">/01</div>
              <h4>The founder raising for the first time.</h4>
              <p>You have traction and a thesis. You do not have a CFO, an investor network, or the bandwidth to run a process while shipping product.</p>
              <div className="av-who-stage">Stage · Pre-seed to Seed</div>
            </div>
            <div className="av-who-card">
              <div className="av-who-marker">/02</div>
              <h4>The growth-stage company doing it again.</h4>
              <p>You have raised once. You know how brutal it was. This time you want a process that runs alongside the company, not consuming the CEO for six months.</p>
              <div className="av-who-stage">Stage · Series A to Series B</div>
            </div>
            <div className="av-who-card">
              <div className="av-who-marker">/03</div>
              <h4>The SME ready for institutional finance.</h4>
              <p>Your business is real, profitable, and growing. You need finance leadership that matches your ambition without the cost of a full-time CFO.</p>
              <div className="av-who-stage">Stage · SME · post-revenue</div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================
          CONTACT (form will be wired to Netlify Forms in the next step)
          ============================================================ */}
      <section id="contact" className="av-contact">
        <div className="av-container">
          <div className="av-contact-grid">
            <div>
              <div className="av-section-eyebrow">04 / Get in touch</div>
              <h2 className="av-section-title">Tell us what you are building.</h2>
              <p style={{ color: 'var(--text-muted)', marginBottom: '32px', lineHeight: '1.6' }}>
                Quick form. We respond within one business day. If your situation is time-sensitive, such as a fundraise on a deadline or an inbound acquisition offer, flag it in the message and we will move faster.
              </p>
              <div className="av-contact-meta">
                <div><strong>Email</strong> <a href="mailto:info@add-ven.com">info@add-ven.com</a></div>
                <div><strong>Hours</strong> Sun to Thu · 09:00 to 18:00</div>
              </div>
            </div>
            <form className="av-form" name="contact" method="POST" data-netlify="true" data-netlify-honeypot="bot-field" onSubmit={handleSubmit}>
              <input type="hidden" name="form-name" value="contact" />
              <p style={{ display: 'none' }}>
                <label>Don't fill this out: <input name="bot-field" /></label>
              </p>
              <div className="av-form-row">
                <label>Name</label>
                <input type="text" name="name" placeholder="Your full name" required />
              </div>
              <div className="av-form-row">
                <label>Company</label>
                <input type="text" name="company" placeholder="Company name" required />
              </div>
              <div className="av-form-row">
                <label>Email</label>
                <input type="email" name="email" placeholder="you@company.com" required />
              </div>
              <div className="av-form-row">
                <label>Stage</label>
                <select name="stage" required>
                  <option>Pre-seed</option>
                  <option>Seed</option>
                  <option>Series A</option>
                  <option>Series B</option>
                  <option>Growth or Series C+</option>
                  <option>SME, post-revenue</option>
                  <option>Considering M&amp;A</option>
                </select>
              </div>
              <div className="av-form-row">
                <label>What do you need?</label>
                <select name="need" required>
                  <option>Financial Modeling</option>
                  <option>Pitch Deck</option>
                  <option>Data Room Setup</option>
                  <option>Cap Table</option>
                  <option>M&amp;A Advisory</option>
                  <option>Market Study</option>
                  <option>Not sure or other</option>
                </select>
              </div>
              <div className="av-form-row">
                <label>Budget for this engagement</label>
                <select name="budget" required>
                  <option>Under $5,000</option>
                  <option>$5,000 to $15,000</option>
                  <option>$15,000 to $30,000</option>
                  <option>$30,000 to $50,000</option>
                  <option>$50,000+</option>
                  <option>Not sure yet, open to discuss</option>
                </select>
              </div>
              <div className="av-form-row">
                <label>Brief context</label>
                <textarea name="message" placeholder="Tell us a bit about your situation" required></textarea>
              </div>
              <button type="submit" disabled={status === 'sending'}>
                {status === 'sending' ? 'Sending...' : status === 'success' ? 'Thank you, we will be in touch.' : 'Send'}
              </button>
              {status === 'error' && (
                <p style={{ color: '#9D2D2D', fontSize: '13px', marginTop: '12px' }}>
                  Something went wrong. Please email us directly at info@add-ven.com.
                </p>
              )}
            </form>
          </div>
        </div>
      </section>

      {/* ============================================================
          FOOTER
          ============================================================ */}
      <footer className="av-footer">
        <div className="av-container av-footer-inner">
          <div className="av-footer-brand">AddVentures</div>
          <div className="av-footer-meta">© AddVentures 2026</div>
        </div>
      </footer>
    </div>
  );
}

export default HomeScreen;